import type CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import type { PriceRecommendationState } from '@app/model/market_state/PriceRecommendationState';
import type MarketStateDailySnapshotState from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/MarketStateDailySnapshotState';

export type DateRowRecommendationCandidate<Recommendation, RoomMappingType> = {
	recommendation: Recommendation;
	roomMappingType: RoomMappingType;
	price: number | string | null | undefined;
};

export type DateRowRecommendationSelection<Recommendation> = {
	recommendation: Recommendation;
	showRecommendedPrice: boolean;
};

const parseFinitePrice = (price: number | string | null | undefined): number | undefined => {
	if (price === null || price === undefined || price === '') return undefined;

	const parsedPrice = Number(price);
	return Number.isFinite(parsedPrice) ? parsedPrice : undefined;
};

export const selectDateRowRecommendation = <Recommendation, RoomMappingType>(
	mainRoomMappingType: RoomMappingType | undefined,
	candidates: DateRowRecommendationCandidate<Recommendation, RoomMappingType>[]
): DateRowRecommendationSelection<Recommendation> | undefined => {
	if (mainRoomMappingType === undefined) return undefined;

	const mainRoomRecommendation = candidates.find(
		candidate =>
			candidate.roomMappingType === mainRoomMappingType &&
			parseFinitePrice(candidate.price) !== undefined
	);
	if (mainRoomRecommendation) {
		return {
			recommendation: mainRoomRecommendation.recommendation,
			showRecommendedPrice: true,
		};
	}

	let cheapestRecommendedRoom:
		| { candidate: DateRowRecommendationCandidate<Recommendation, RoomMappingType>; price: number }
		| undefined;
	for (const candidate of candidates) {
		const price = parseFinitePrice(candidate.price);
		if (price === undefined) continue;
		if (!cheapestRecommendedRoom || price < cheapestRecommendedRoom.price) {
			cheapestRecommendedRoom = { candidate, price };
		}
	}

	if (!cheapestRecommendedRoom) return undefined;

	return {
		recommendation: cheapestRecommendedRoom.candidate.recommendation,
		showRecommendedPrice: false,
	};
};

// The LocalDateTime scalar emits ISO timestamps without an offset. Normalize their
// fractional seconds before comparing: Date alone would discard sub-millisecond precision.
const normalizeSnapshotTimestamp = (value: string | null | undefined): string | undefined => {
	if (typeof value !== 'string') return undefined;
	const match = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d{1,9}))?Z?$/.exec(value);
	if (!match) return undefined;
	const [, seconds, fraction = ''] = match;
	const parsed = new Date(`${seconds}Z`);
	if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 19) !== seconds) return undefined;
	return `${seconds}.${fraction.padEnd(9, '0')}`;
};

export const findValidRecommendationStates = (
	dailySnapshotState: MarketStateDailySnapshotState,
	companyHotel: CompanyHotel,
	canViewRecommendations: boolean,
	today?: string
): PriceRecommendationState[] => {
	if (!canViewRecommendations || (today === undefined ? dailySnapshotState.calendarDay.isPast : dailySnapshotState.calendarDay.display < today)) return [];
	if (!companyHotel.hasRecommendationNormalEngineRoompulseOrBraintree()) return [];
	const marketTimestamp = normalizeSnapshotTimestamp(dailySnapshotState.marketStateDailySnapshot?.recommendationMarketUpdateDate);
	if (marketTimestamp === undefined) return [];

	return (dailySnapshotState.marketStateDailySnapshot?.allPriceRecommendationStates || [])
		.filter(state => {
			const recommendationTimestamp = normalizeSnapshotTimestamp(state.creationDate);
			return state.isValid && recommendationTimestamp !== undefined && recommendationTimestamp > marketTimestamp;
		});
};

export const findSubmittableRecommendationStates = (
	dailySnapshotState: MarketStateDailySnapshotState,
	companyHotel: CompanyHotel,
	canViewRecommendations: boolean,
	today?: string
): PriceRecommendationState[] => {
	const submittedRecommendationStateIds = new Set(
		dailySnapshotState.marketStateDailySnapshot?.submittedRecommendationStateIds || []
	);

	return findValidRecommendationStates(dailySnapshotState, companyHotel, canViewRecommendations, today)
		.filter(state => !submittedRecommendationStateIds.has(state.id));
};
export const findDateRowRecommendation = (
	dailySnapshotState: MarketStateDailySnapshotState,
	recommendationStates: PriceRecommendationState[]
) => {
	const mainHotelSnapshot =
		dailySnapshotState.marketStateDailySnapshot?.current.findDailyMainHotelSnapshot();
	const mainRoomMappingType = mainHotelSnapshot?.getLowestItemForRoom()?.roomMappingType;

	const candidates = recommendationStates.map(recommendation => ({
		recommendation,
		roomMappingType: recommendation.roomMappingType,
		price: mainHotelSnapshot?.getLowestItemForRoomMappingType(recommendation.roomMappingType)
			?.lowestItem?.item?.price?.amount,
	}));
	// A submitted recommendation keeps its display, with or without *; the Submitted tick marks it.
	return selectDateRowRecommendation(mainRoomMappingType, candidates);
};
